import { useEffect, useState } from 'react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { toast } from 'react-toastify'
import apiClient from '../../services/apiClient'

export type CaseStudyViewTarget = {
  userId: number
  courseId?: number | null
  studentName?: string | null
} | null

type CaseStudyData = {
  id: number
  name: string
  businessProblem: string
  domain: string
  courseName?: string
  courseCode?: string
  studentName?: string
  studentEmail?: string
  updatedAt?: string
}

type Props = {
  target: CaseStudyViewTarget
  onClose: () => void
}

export default function AdminStudentCaseStudyModal({ target, onClose }: Props) {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<CaseStudyData | null>(null)

  useEffect(() => {
    if (!target?.userId) {
      setData(null)
      return
    }
    let cancelled = false
    ;(async () => {
      try {
        setLoading(true)
        setData(null)
        const res = await apiClient.get(`/admin/users/${target.userId}/homework-case-study`, {
          params: target.courseId ? { courseId: target.courseId } : undefined,
        })
        if (!cancelled) setData(res.data?.data || null)
      } catch (e: any) {
        if (!cancelled) {
          toast.error(e?.response?.data?.message || e?.message || 'Không tải được case study')
          onClose()
        }
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [target?.userId, target?.courseId])

  if (!target) return null

  const titleName = data?.studentName || target.studentName || `HV #${target.userId}`

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        className="absolute inset-0 bg-black/40"
        aria-label="Đóng"
        onClick={onClose}
      />
      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border border-m2-outline-variant bg-white shadow-xl">
        <div className="sticky top-0 flex items-start justify-between gap-3 border-b bg-white px-4 py-3">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-eh-forest-ink">Case study học viên</h2>
            <p className="text-sm text-m2-on-surface-variant truncate">{titleName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-m2-outline-variant hover:bg-m2-surface-high"
            aria-label="Đóng"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="px-4 py-4">
          {loading ? (
            <p className="text-sm text-m2-on-surface-variant">Đang tải...</p>
          ) : !data ? (
            <p className="text-sm text-m2-on-surface-variant">Không có dữ liệu case study.</p>
          ) : (
            <div className="space-y-4 text-sm">
              {(data.courseName || data.courseCode) && (
                <p className="text-m2-on-surface-variant">
                  Khóa: {data.courseCode ? `${data.courseCode} — ` : ''}
                  {data.courseName}
                </p>
              )}
              <div>
                <div className="font-medium text-eh-forest-ink mb-1">Tên case study</div>
                <p className="rounded-md border bg-m2-surface-high/40 px-3 py-2">{data.name}</p>
              </div>
              <div>
                <div className="font-medium text-eh-forest-ink mb-1">Domain</div>
                <p className="rounded-md border bg-m2-surface-high/40 px-3 py-2">{data.domain}</p>
              </div>
              <div>
                <div className="font-medium text-eh-forest-ink mb-1">Vấn đề doanh nghiệp</div>
                <div
                  className="prose prose-sm max-w-none rounded-md border bg-m2-surface-high/40 px-3 py-2"
                  dangerouslySetInnerHTML={{ __html: data.businessProblem || '' }}
                />
              </div>
              {data.updatedAt && (
                <p className="text-xs text-m2-on-surface-variant">
                  Cập nhật: {new Date(data.updatedAt).toLocaleString('vi-VN')}
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
